import { useState } from "react";
import type { ApiKeyField } from "../../../types/integration";
import styles from "./ByoOauthConnect.module.css";

// IP-422 self-serve BYO OAuth (customer-owned app, e.g. their Meta Ads app).
// The credentials modal reuses ApiKeyConnectModal with these fields plus the
// preamble below.

// Field names match what Composio's authConfigs.create expects for a custom
// OAuth2 config — they pass through ztc-core and the gateway unchanged.
export const BYO_OAUTH_FIELDS: ReadonlyArray<ApiKeyField> = [
  { name: "client_id", displayName: "App ID (client ID)", type: "string", description: null },
  { name: "client_secret", displayName: "App secret (client secret)", type: "string", description: null },
];

// Composio hosts the OAuth callback for custom auth configs; the customer must
// allow-list this exact URL in their own app's OAuth settings. NOTE: Composio's
// docs say /api/v3.1/toolkits/auth/callback, but the redirect_uri they actually
// send is this one — verified against a live Meta Ads connect (2026-07-14,
// IP-422). Facebook requires an exact match, so the docs URL alone fails with
// "URL Blocked".
export const COMPOSIO_OAUTH_REDIRECT_URI =
  "https://backend.composio.dev/api/v1/auth-apps/add";

type PreambleProps = {
  // Integration title for the copy ("Meta Ads").
  readonly title: string;
};

// Setup instructions rendered above the credential fields: which app to use
// and a copy button for the redirect URL. The URL itself stays hidden (it's
// long and only ever pasted, never read) unless the clipboard API is blocked,
// in which case it appears as selectable text so manual copy still works.
export const ByoOauthPreamble = ({ title }: PreambleProps): JSX.Element => {
  const [copied, setCopied] = useState<boolean>(false);
  const [showUrl, setShowUrl] = useState<boolean>(false);
  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(COMPOSIO_OAUTH_REDIRECT_URI);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setShowUrl(true);
    }
  };
  return (
    <div className={styles.preamble}>
      <p className={styles.text}>
        {title} connects through your own {title} app. Copy our redirect URL into your
        app&apos;s OAuth settings, then paste the app&apos;s credentials below.
      </p>
      <button type="button" className={styles.copyBtn} onClick={() => void copy()}>
        <CopyIcon />
        {copied ? "Copied to clipboard" : "Copy redirect URL"}
      </button>
      {showUrl ? <code className={styles.redirectUri}>{COMPOSIO_OAUTH_REDIRECT_URI}</code> : null}
      <p className={styles.note}>
        Use the same account that created the app when approving access on the next screen.
      </p>
    </div>
  );
};

const CopyIcon = (): JSX.Element => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);
